import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import styles from "./PortfolioItemAttachment.module.scss"
import { connect } from "react-redux"
import { getPortfolioQuestions } from "../../../store/actions"
import { ReactComponent as ClockIcon } from "../../../assets/svg/clockIcon.svg"
import { ReactComponent as ProductIcon } from "../../../assets/svg/ProductIcon.svg"
import { ReactComponent as ServiceIcon } from "../../../assets/svg/ServiceIcon.svg"
import { X, MapPin, Calendar, MessageCircle, ChevronLeft, ChevronRight, ImageIcon, User } from 'lucide-react'
import useUserImage from "../../../hooks/useUserImage"
import { ReactComponent as NoImage } from "../../../assets/svg/noImageIcon.svg"
import { useNavigate } from "react-router-dom"


const backdrop = {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
}

const modal = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { delay: 0.1 },
    },
}

const PortfolioItemAttachment = ({ item, userData, closeModal, getPortfolioQuestions, portfolioQuestions }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)

    const navigate = useNavigate()

    const {
        id,
        name,
        description,
        startDate,
        endDate,
        zone,
        type,
        price,
        priceUnit,
        currencyType,
        portfolio_images,
        createdAt,
        user,
    } = item

    const hasImages = portfolio_images && portfolio_images.length > 0
    const { imageError, initials, profileImage } = useUserImage(user)
    const { name: userName, last_name, razon_social, type: userType } = user || {}
    const completeName = userType === "COMPANY" ? razon_social : `${userName} ${last_name}`

    useEffect(() => {
        if (id) {
            getPortfolioQuestions(id)
        }
    }, [id, getPortfolioQuestions])

    const formatDate = (dateString) => {
        if (!dateString) return null
        const date = new Date(dateString)
        return date.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        })
    }

    const formatPrice = () => {
        if (!price) return null
        const currency = currencyType === "USD" ? "US$" : "$"
        const unit = priceUnit ? `/${priceUnit}` : ""
        return `${currency} ${price.toLocaleString()}${unit}`
    }

    const nextImage = () => {
        if (hasImages && portfolio_images.length > 1) {
            setCurrentImageIndex((prev) => (prev === portfolio_images.length - 1 ? 0 : prev + 1))
        }
    }

    const prevImage = () => {
        if (hasImages && portfolio_images.length > 1) {
            setCurrentImageIndex((prev) => (prev === 0 ? portfolio_images.length - 1 : prev - 1))
        }
    }

    const portfolioQuestionsForItem = portfolioQuestions[id] || []

    const handleRedirectToProfile = () => {
        navigate(`/perfil/${user.id}`)
    }


const imageUrl = encodeURI(portfolio_images[currentImageIndex]?.image);

    return (
        <AnimatePresence>
            <motion.div
                className={styles.modal_overlay}
                onClick={closeModal}
                variants={backdrop}
                initial="hidden"
                animate="visible"
                exit="hidden"
            >
                <motion.div
                    className={styles.modal_container}
                    onClick={(e) => e.stopPropagation()}
                    variants={modal}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                >
                    <div className={styles.modal_header}>
                        <div className={styles.modal_header_content}>
                            <div className={styles.modal_badge}>
                                {type === "producto" ? <ProductIcon width={16} height={16} /> : <ServiceIcon width={16} height={16} />}
                                <span>{type === "producto" ? "Producto" : "Servicio"}</span>
                            </div>
                            {formatPrice() && (
                                <div className={styles.modal_price}>
                                    <span>{formatPrice()}</span>
                                </div>
                            )}
                        </div>
                        <button className={styles.modal_close} onClick={closeModal}>
                            <X size={24} />
                        </button>
                    </div>

                    <div className={styles.modal_content}>
                        <div className={styles.gallery_section}>
                            {hasImages ? (
                                <div className={styles.gallery}>
                                    <div
                                        className={styles.gallery_main}
                                        style={{ 
                                            "--bg-image": imageUrl ? `url(${imageUrl})` : 'none' 
                                          }}
                                    >
                                        <img
                                            src={portfolio_images[currentImageIndex]?.image || "/placeholder.svg"}
                                            alt={name}
                                            className={styles.gallery_image}
                                        />
                                        {portfolio_images.length > 1 && (
                                            <>
                                                <button className={styles.gallery_nav_prev} onClick={prevImage}>
                                                    <ChevronLeft size={24} />
                                                </button>
                                                <button className={styles.gallery_nav_next} onClick={nextImage}>
                                                    <ChevronRight size={24} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {portfolio_images.length > 1 && (
                                        <div className={styles.gallery_thumbnails}>
                                            {portfolio_images.map((img, index) => (
                                                <img
                                                    key={index}
                                                    src={img.image || "/placeholder.svg"}
                                                    alt={`${name} ${index + 1}`}
                                                    className={`${styles.gallery_thumbnail} ${index === currentImageIndex ? styles.active : ""}`}
                                                    onClick={() => setCurrentImageIndex(index)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={styles.gallery_mock}>
                                    <div className={styles.gallery_mock_container}>
                                        <div className={styles.gallery_mock_icon}>
                                            <ImageIcon size={64} />
                                        </div>
                                        <div className={styles.gallery_mock_text}>
                                            <span>{type === "producto" ? "Producto" : "Servicio"}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={styles.info_section}>
                            <div className={styles.info_content}>
                                <h2 className={styles.info_title}>{name}</h2>

                                {description && <p className={styles.info_description}>{description}</p>}

                                <div className={styles.info_details}>
                                    {zone && (
                                        <div className={styles.info_detail}>
                                            <MapPin size={16} />
                                            <span>{zone}</span>
                                        </div>
                                    )}

                                    {(startDate || endDate) && (
                                        <div className={styles.info_detail}>
                                            <Calendar size={16} />
                                            <span>
                                                {startDate && formatDate(startDate)}
                                                {startDate && endDate && " - "}
                                                {endDate && formatDate(endDate)}
                                            </span>
                                        </div>
                                    )}

                                    <div className={styles.info_detail}>
                                        <ClockIcon width={16} height={16} />
                                        <span>{formatDate(createdAt)}</span>
                                    </div>
                                </div>
                                <div className={styles.info_questions}>
                                    <div className={styles.info_questions_header}>
                                        <MessageCircle size={18} />
                                        <span>
                                            {portfolioQuestionsForItem.length > 0
                                                ? `${portfolioQuestionsForItem.length} ${portfolioQuestionsForItem.length === 1 ? "pregunta" : "preguntas"}`
                                                : "Preguntas"
                                            }
                                        </span>
                                    </div>
                                    <div className={styles.info_questions_list}>
                                        {portfolioQuestionsForItem.length > 0 ? (
                                            portfolioQuestionsForItem.map((question, index) => (
                                                <div key={index} className={styles.question_item}>
                                                    <p className={styles.question_text}>{question.question}</p>
                                                    {question.answers && question.answers.length > 0 && (
                                                        <p className={styles.question_answer}>{question.answers[0].answer}</p>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className={styles.question_item}>
                                                <p className={styles.question_text}>No hay preguntas aún</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>

                    </div>
                    <div className={styles.info_footer}>
                        <div className={styles.user_info} >
                            <div className={styles.user_avatar} onClick={handleRedirectToProfile}>
                                {!imageError && profileImage ? (
                                    <img className={styles.user_avatar_img} src={profileImage || "/placeholder.svg"} alt="user" />
                                ) : initials ? (
                                    <div className={styles.user_avatar_initials}>{initials}</div>
                                ) : (
                                    <div className={styles.user_avatar_img}>
                                        <NoImage width={40} height={40} />
                                    </div>
                                )}
                            </div>
                            <div className={styles.user_details} onClick={handleRedirectToProfile}>
                                <p className={styles.user_name}>{completeName}</p>
                                <div className={styles.user_badge}>
                                    <User size={12} />
                                    <span>{userType === "COMPANY" ? "Empresa" : "Profesional"}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}

const mapStateToProps = (state) => {
    return {
        portfolioQuestions: state.profile.portfolioQuestions,
    }
}

export default connect(mapStateToProps, { getPortfolioQuestions })(PortfolioItemAttachment)